import type { Declaration, Expression, GlossaProgram, Statement, Subprogram, SwitchCaseValue } from '../parser/ast.js';

/**
 * Μια πραγματική παράμετρος πρέπει να είναι πίνακας ΑΚΡΙΒΩΣ όταν η
 * αντίστοιχη τυπική είναι δηλωμένη πίνακας (και το αντίστροφο) — βλ.
 * `Interpreter.requireMatchingParameterKind` για το runtime αντίστοιχο.
 * Αυτός ο έλεγχος τρέχει ΠΡΙΝ την εκτέλεση (ίδια λογική με
 * `findUndeclaredGlossaReference`) ώστε να πιάνει το λάθος ΑΚΟΜΑ ΚΙ αν
 * βρίσκεται σε κλάδο/βρόχο που δεν θα εκτελούνταν σε αυτό το συγκεκριμένο
 * τρέξιμο.
 *
 * "Απόρριψη αντί για μάντεμα": ελέγχεται ΜΟΝΟ όταν το όρισμα έχει ΣΤΑΤΙΚΑ
 * αναγνωρίσιμο "είδος" — όνομα ήδη δηλωμένης μεταβλητής/πίνακα/σταθεράς,
 * στοιχείο πίνακα (πάντα απλή τιμή), ή γραμματικό literal (πάντα απλή
 * τιμή). Εκφράσεις όπως πράξεις ή κλήσεις συναρτήσεων αφήνονται στο
 * runtime check.
 */
export interface ParameterKindMismatch {
  readonly parameterName: string;
  readonly expectedKind: 'πίνακας' | 'απλή τιμή';
  readonly line: number;
  readonly column: number;
}

function findDeclaredKind(declarations: readonly Declaration[], name: string): { readonly isArray: boolean } | undefined {
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') {
      if (decl.name === name) return { isArray: false };
      continue;
    }
    for (const declarator of decl.declarators) {
      if (declarator.name === name) return { isArray: declarator.dimensions.length > 0 };
    }
  }
  return undefined;
}

function staticArgumentKind(expr: Expression, declarations: readonly Declaration[]): { readonly isArray: boolean } | null {
  switch (expr.kind) {
    case 'Identifier':
      return findDeclaredKind(declarations, expr.name) ?? null;
    case 'ArrayAccess':
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
      return { isArray: false };
    default:
      return null;
  }
}

function checkCallArguments(
  sub: Subprogram,
  args: readonly Expression[],
  declarations: readonly Declaration[],
): ParameterKindMismatch | null {
  const count = Math.min(args.length, sub.parameters.length);
  for (let i = 0; i < count; i++) {
    const paramName = sub.parameters[i]!;
    const paramInfo = findDeclaredKind(sub.declarations, paramName);
    if (paramInfo === undefined) continue;
    const argExpr = args[i]!;
    const argKind = staticArgumentKind(argExpr, declarations);
    if (argKind === null || argKind.isArray === paramInfo.isArray) continue;
    return {
      parameterName: paramName,
      expectedKind: paramInfo.isArray ? 'πίνακας' : 'απλή τιμή',
      line: argExpr.line,
      column: argExpr.column,
    };
  }
  return null;
}

function checkExpr(
  expr: Expression,
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ParameterKindMismatch | null {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
    case 'Identifier':
      return null;
    case 'ArrayAccess': {
      for (const idx of expr.indices) {
        const result = checkExpr(idx, declarations, subprograms);
        if (result !== null) return result;
      }
      return null;
    }
    case 'FunctionCall': {
      for (const arg of expr.args) {
        const result = checkExpr(arg, declarations, subprograms);
        if (result !== null) return result;
      }
      const sub = subprograms.get(expr.callee.name);
      // Έτοιμη μαθηματική συνάρτηση ή άγνωστο όνομα -- ελέγχεται αλλού.
      if (sub === undefined) return null;
      return checkCallArguments(sub, expr.args, declarations);
    }
    case 'UnaryExpression':
      return checkExpr(expr.operand, declarations, subprograms);
    case 'BinaryExpression': {
      const left = checkExpr(expr.left, declarations, subprograms);
      if (left !== null) return left;
      return checkExpr(expr.right, declarations, subprograms);
    }
  }
}

function checkSwitchCaseValue(
  value: SwitchCaseValue,
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ParameterKindMismatch | null {
  if (value.kind === 'CaseRange') {
    const from = checkExpr(value.from, declarations, subprograms);
    if (from !== null) return from;
    return checkExpr(value.to, declarations, subprograms);
  }
  if (value.kind === 'CaseComparison') return checkExpr(value.value, declarations, subprograms);
  return checkExpr(value, declarations, subprograms);
}

function checkStatement(
  stmt: Statement,
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ParameterKindMismatch | null {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return checkExpr(stmt.value, declarations, subprograms);
    case 'ReadStatement':
    case 'SwapStatement':
      return null;
    case 'WriteStatement': {
      for (const value of stmt.values) {
        const result = checkExpr(value, declarations, subprograms);
        if (result !== null) return result;
      }
      return null;
    }
    case 'IfStatement': {
      const condition = checkExpr(stmt.condition, declarations, subprograms);
      if (condition !== null) return condition;
      const thenResult = checkStatements(stmt.thenBranch, declarations, subprograms);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchCondition = checkExpr(branch.condition, declarations, subprograms);
        if (branchCondition !== null) return branchCondition;
        const branchResult = checkStatements(branch.body, declarations, subprograms);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, declarations, subprograms);
      return null;
    }
    case 'ForStatement': {
      const from = checkExpr(stmt.from, declarations, subprograms);
      if (from !== null) return from;
      const to = checkExpr(stmt.to, declarations, subprograms);
      if (to !== null) return to;
      if (stmt.step !== null) {
        const step = checkExpr(stmt.step, declarations, subprograms);
        if (step !== null) return step;
      }
      return checkStatements(stmt.body, declarations, subprograms);
    }
    case 'WhileStatement': {
      const condition = checkExpr(stmt.condition, declarations, subprograms);
      if (condition !== null) return condition;
      return checkStatements(stmt.body, declarations, subprograms);
    }
    case 'RepeatUntilStatement': {
      const bodyResult = checkStatements(stmt.body, declarations, subprograms);
      if (bodyResult !== null) return bodyResult;
      return checkExpr(stmt.condition, declarations, subprograms);
    }
    case 'SwitchStatement': {
      const discriminant = checkExpr(stmt.discriminant, declarations, subprograms);
      if (discriminant !== null) return discriminant;
      for (const switchCase of stmt.cases) {
        for (const value of switchCase.values) {
          const valueResult = checkSwitchCaseValue(value, declarations, subprograms);
          if (valueResult !== null) return valueResult;
        }
        const bodyResult = checkStatements(switchCase.body, declarations, subprograms);
        if (bodyResult !== null) return bodyResult;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, declarations, subprograms);
      return null;
    }
    case 'CallStatement': {
      for (const arg of stmt.args) {
        const result = checkExpr(arg, declarations, subprograms);
        if (result !== null) return result;
      }
      const sub = subprograms.get(stmt.callee.name);
      if (sub === undefined) return null;
      return checkCallArguments(sub, stmt.args, declarations);
    }
  }
}

function checkStatements(
  statements: readonly Statement[],
  declarations: readonly Declaration[],
  subprograms: ReadonlyMap<string, Subprogram>,
): ParameterKindMismatch | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, declarations, subprograms);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ ασυμφωνία είδους παραμέτρου (με σειρά εμφάνισης —
 * κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα), ή `null` αν δεν υπάρχει
 * καμία. Κάθε σημείο κλήσης ελέγχεται με τις δηλώσεις του ΔΙΚΟΥ ΤΟΥ scope
 * (κύριο πρόγραμμα ή το υποπρόγραμμα μέσα στο οποίο βρίσκεται η κλήση).
 */
export function findParameterKindMismatch(program: GlossaProgram): ParameterKindMismatch | null {
  const subprograms = new Map<string, Subprogram>();
  for (const sub of program.subprograms) subprograms.set(sub.name, sub);

  const mainResult = checkStatements(program.body, program.declarations, subprograms);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const subResult = checkStatements(sub.body, sub.declarations, subprograms);
    if (subResult !== null) return subResult;
  }
  return null;
}
